import React from 'react';
import MagazineStageSimple from '../../Components/MagazineStageSimple/MagazineStageSimple';
import MagazineTextBlock from '../../Components/MagazineTextBlock/MagazineTextBlock';
import imgCapa from '../../assets/news-events/wear_and_tear/Capa.png';
import imgDestacada from '../../assets/news-events/wear_and_tear/Destacada.png';
import imgImagem1 from '../../assets/news-events/wear_and_tear/Imagem 1.png';
import imgImagem2 from '../../assets/news-events/wear_and_tear/Imagem 2.png';
import imgImagem3 from '../../assets/news-events/wear_and_tear/Imagem 3.png';
import imgPost from '../../assets/news-events/wear_and_tear/Post.png';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

const WearAndTear = () => {
    const { t } = useTranslation();

    const ogTitle = t('Wear and Tear on Mining Equipment');
    const ogDescription = t(
        'Learn how to recognize early wear-and-tear signals in hydraulic systems and how preventive maintenance helps reduce downtime in mining operations.'
    );
    const ogImage = `https://www.hansa-flex.com.br/${imgCapa}`;
    const ogUrl = `https://www.hansa-flex.com.br/wear-and-tear-mining-equipment`;

    return (
        <div>
            <Helmet>
                <meta property="og:title" content={ogTitle} />
                <meta property="og:description" content={ogDescription} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:url" content={ogUrl} />
                <meta property="og:type" content="News" />
            </Helmet>

            <MagazineStageSimple
                imageSrc={imgCapa}
                imageAlt="Wear and Tear on Mining Equipment: How to Predict Failures"
                meta="24.03.2026 | Gabriela Nascimento"
                title="Wear and Tear on Mining Equipment: How to Predict Failures"
                text="Unexpected downtime in mining equipment doesn’t happen “out of the blue.” In most cases, the hydraulic system had already been showing clear signs of wear and tear. Knowing how to identify those warning signs before a failure occurs can mean higher productivity, lower costs, and a much safer operation."
                readingTimeMinutes={6}
            />

            <MagazineTextBlock
                paragraphs={[
                    'In this article, you’ll learn how to recognize these signs and how preventive maintenance can transform the performance of your assets.',
                    'Equipment such as excavators, drills, and dump trucks rely directly on high-pressure hydraulic systems to operate efficiently in extreme environments. Abrasive dust, intense heat, and continuous load cycles accelerate component wear.',
                    'In this scenario, companies that adopt preventive maintenance strategies can anticipate problems and maintain operational continuity with greater predictability.',
                ]}
            />

            <MagazineTextBlock
                imageSrc={imgImagem1}
                imageAlt="The role of hydraulic systems in mining"
                title="The role of hydraulic systems in mining"
                paragraphs={[
                    'In mining, hydraulics are responsible for converting energy into force and precise movements. Among the systems most exposed to wear are:',
                ]}
                bulletPoints={[
                    { point: 'Hydraulic excavators:', paragraph: 'essential for loading and selective blasting.' },
                    { point: 'Wheel loaders:', paragraph: 'ensure mobility and efficiency in ore transport.' },
                    { point: 'Hydraulic drills:', paragraph: 'operate under high pressure to drill holes in rock.' },
                    { point: 'Dump trucks:', paragraph: 'use hydraulic cylinders for unloading material.' },
                    { point: 'Hydraulic breakers:', paragraph: 'break up large rocks.' },
                ]}
                paragraphs2={["Components such as pumps, cylinders, valves, and hoses operate under pressures that can reach 700 bar, making any failure potentially critical to the operation."]}
                bulletType="numbers"
            />

            <MagazineTextBlock
                title="How to identify early signs of wear before failure"
                boldPara="Operational signs:"
                paragraphs={["Watch for changes in performance:"]}
                bulletPoints={[
                    { paragraph: "Sluggishness or loss of power in actuators" },
                    { paragraph: "Drop in system pressure" },
                    { paragraph: "Constant oil overheating" },
                    { paragraph: "Frequent leaks in hoses and connections" },
                    { paragraph: "Premature failures in pumps or cylinders" },
                ]}
                paragraphs2={["These symptoms indicate wear on seals, system contamination, or operational overload."]}
                bulletType="bullets"
            />

            <MagazineTextBlock
                imageSrc={imgImagem2}
                imageAlt="More warning signs to monitor"
                boldPara="Sensory signs"
                paragraphs={["Noises and vibrations are important indicators:"]}
                bulletPoints={[
                    { paragraph: "High-pitched squeals suggest excessive friction" },
                    { paragraph: "Popping sounds may indicate overload or contamination" },
                    { paragraph: "Excessive vibration may be linked to misalignment" },
                ]}
                bulletType="bullets"
            />

            <MagazineTextBlock
                boldPara="Signs in hydraulic fluid analysis"
                paragraphs={["Hydraulic oil acts as a “thermometer” for the system:"]}
                bulletPoints={[
                    { paragraph: "Presence of metal particles or dust" },
                    { paragraph: "Moisture or combustion residues" },
                    { paragraph: "Frequent filter changes" },
                ]}
                paragraphs2={["Solid contamination is one of the main causes of wear in mining, due to the abrasiveness of the materials and extreme environmental conditions."]}
            />

            <MagazineTextBlock
                title="Why Preventive Maintenance Is a Game-Changer"
                paragraphs={[
                    "Adopting preventive maintenance means taking action before a failure occurs. This includes regular inspections, scheduled component replacement, and planning based on operating cycles.",
                    "With specialized technical support from HANSA-FLEX, we develop customized plans that deliver clear benefits:",
                ]}
                bulletPoints={[
                    { point: "Increased production:", paragraph: "Fewer unexpected downtimes improve asset availability." },
                    { point: "Cost reduction:", paragraph: "avoids emergency repairs and optimizes parts inventory." },
                    { point: "Longer service life:", paragraph: "planned replacements reduce accumulated wear and tear." },
                    { point: "Operational quality:", paragraph: "stable equipment generates less rework." },
                    { point: "Enhanced safety:", paragraph: "well-maintained systems reduce the risk of accidents." },
                ]}
                paragraphs2={["We have been specialists in hydraulics for over 60 years, with a global presence and more than 150,000 active customers served by a network of over 460 branches. We offer integrated fluid technology solutions to make your operation safer and more efficient."]}
                bulletType="bullets"
            />

            <MagazineTextBlock
                imageSrc={imgImagem3}
                imageAlt="The decision to safeguard your operation"
                title="The decision to safeguard your operation"
                paragraphs={["In mining, every minute counts. Anticipating wear and tear on hydraulic systems is not just a technical issue, but a strategic one. Operations that monitor early warning signs and invest in preventive maintenance gain predictability, competitiveness, and confidence in their day-to-day operations.",
                    "Contact the experts at HANSA-FLEX and find out how to create a preventive maintenance plan tailored to your equipment."]}
                buttonName="Contact us"
                buttonLink="/contact-us"
            />

            <div style={{ backgroundColor: '#f8f8f8' }}>
                <br />
                <br />
                <br />
            </div>
        </div>
    );
};

export default WearAndTear;